import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { brandApi} from "@/resources/brand";
import { Brand, CreateBrandParams } from "@/types";
export type { Brand } from "@/types";

export interface BrandGroupPreset {
  id: string;
  name: string;
  brandIds: string[];
  isDefault?: boolean;
}

interface AppDataContextType {
  brands: Brand[];
  isLoadingBrands: boolean;
  selectedBrandId: number | null;
  setSelectedBrandId: (brandId: number) => void;
  addBrand: (brand: CreateBrandParams, onSuccess?: () => void) => void;
  deleteBrand: (brandId: number) => void;

  brandGroupPresets: BrandGroupPreset[];
  addBrandGroupPreset: (preset: Omit<BrandGroupPreset, "id">) => void;
  updateBrandGroupPreset: (id: string, updates: Partial<Omit<BrandGroupPreset, "id">>) => void;
  deleteBrandGroupPreset: (id: string) => void;
}

const BRAND_ID_LOCAL_STORAGE_KEY = "brandId";

const readBrandIdFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem(BRAND_ID_LOCAL_STORAGE_KEY);
    if (!raw) return null;

    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { data: brands = [], isLoading: isLoadingBrands } = brandApi.useGetBrands();
  const {mutate: createBrand} = brandApi.useCreateBrand();
  const {mutate: deleteBrand} = brandApi.useDeleteBrand();

  const [selectedBrandId, setBrandId] = useState<number | null>(readBrandIdFromLocalStorage);
  const [brandGroupPresets, setBrandGroupPresets] = useState<BrandGroupPreset[]>([
    { id: "preset-top", name: "Top Tier Partners", brandIds: ["supergoop"], isDefault: true },
    { id: "preset-mid", name: "Mid Tier Partners", brandIds: ["glossier", "coola"], isDefault: true },
    { id: "preset-low", name: "Low Tier Partners", brandIds: ["newbrand", "thelabco", "kinfield"], isDefault: true },
  ]);

  useEffect(() => {
      if (!selectedBrandId) {
        localStorage.removeItem(BRAND_ID_LOCAL_STORAGE_KEY);
        return;
      }

        localStorage.setItem(BRAND_ID_LOCAL_STORAGE_KEY, String(selectedBrandId));
  }, [selectedBrandId]);

  useEffect(() => {
    if (brands.length > 0 && (selectedBrandId === null || !brands.some((b) => b.id === selectedBrandId))) {
      setBrandId(brands[0].id);
    }
  }, [brands, selectedBrandId]);

  const setSelectedBrandId = (brandId: number) => setBrandId(brandId);

  const addBrand = (brand: CreateBrandParams, onSuccess?: () => void) => {
    createBrand(brand, {
      onSuccess: (newBrand) => { setBrandId(newBrand.id); onSuccess?.(); },
    });
  };

  const onDeleteBrand = (brandId: number) => {
    if (brandId > 0) {
       deleteBrand(brandId);
    }
    setBrandGroupPresets((prev) =>
      prev.map((p) => ({ ...p, brandIds: p.brandIds.filter((id) => id !== String(brandId)) }))
    );
    if (selectedBrandId === brandId) {
      const remaining = brands.filter((b) => b.id !== brandId);
      setBrandId(remaining.length > 0 ? remaining[0].id : null);
    }
  };

  const addBrandGroupPreset = (preset: Omit<BrandGroupPreset, "id">) => {
    const id = `preset-${Date.now()}`;
    setBrandGroupPresets((prev) => [...prev, { ...preset, id }]);
  };

  const updateBrandGroupPreset = (id: string, updates: Partial<Omit<BrandGroupPreset, "id">>) => {
    setBrandGroupPresets((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
    );
  };

  const deleteBrandGroupPreset = (id: string) => {
    setBrandGroupPresets((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <AppDataContext.Provider
      value={{
        brands,
        isLoadingBrands,
        selectedBrandId,
        setSelectedBrandId,
        addBrand,
        deleteBrand: onDeleteBrand,
        brandGroupPresets,
        addBrandGroupPreset,
        updateBrandGroupPreset,
        deleteBrandGroupPreset,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

const noopAppData: AppDataContextType = {
  brands: [],
  isLoadingBrands: false,
  selectedBrandId: null,
  setSelectedBrandId: () => undefined,
  addBrand: () => undefined,
  deleteBrand: () => undefined,
  brandGroupPresets: [],
  addBrandGroupPreset: () => undefined,
  updateBrandGroupPreset: () => undefined,
  deleteBrandGroupPreset: () => undefined,
};

export function useAppData() {
  const context = useContext(AppDataContext);
  return context ?? noopAppData;
}
